'use client';

import { FC, useState, useContext, useMemo, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Radio, ShieldAlert, X } from 'lucide-react';
import { FilterSelect } from '@/components/shared/Filters/FilterSelect';
import { FilterSearch } from '@/components/shared/Filters/FilterSearch';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ProductsContext } from './context';
import ProductsContextProvider from './context';
import ThreadDetails from './components/ThreadDetails';
import ContactPanel from './components/ContactPanel';
import { findPendingInteractionIndex } from './lib/pendingInteraction';
import { isHighConfidenceSpam } from './lib/spamDisposition';
import { getNextVisibleThread, restoreThreadAtIndex } from './lib/mailboxWorkflow';
import { FEEDBACK_FILTER_VALUE } from '@/constants/bot';
import { cn } from '@/lib/utils';
import { socketIo } from '@/contexts/NotificationProvider';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useAuth } from '@/contexts/AuthProvider';
import { useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import LiveVisitorsPanel from './components/LiveVisitorsPanel';
import type { ISpamDisposition } from '@/types';
import { useUpdateInboxDisposition } from '@/hooks/useBot';
import { toast } from 'react-toastify';

interface IMessagesHubProps {
  token: string;
  botSelectorContent?: React.ReactNode;
}

const TIME_FRAME_OPTIONS = [
  { label: 'All time', value: '0' },
  { label: 'Last 24 hours', value: '1' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
];

const FEEDBACK_OPTIONS = [
  { label: 'All feedback', value: String(FEEDBACK_FILTER_VALUE.ALL) },
  { label: 'Positive', value: String(FEEDBACK_FILTER_VALUE.POSITIVE) },
  { label: 'Negative', value: String(FEEDBACK_FILTER_VALUE.NEGATIVE) },
];

const LIVE_WINDOW_MS = 10 * 60 * 1000;

const formatThreadAge = (value?: string) => {
  if (!value) return '';
  const elapsedMs = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(elapsedMs)) return '';
  const minutes = Math.max(0, Math.floor(elapsedMs / 60_000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 365) return `${days}d`;
  return `${Math.floor(days / 365)}y`;
};

const MessagesHubContent: FC<{ token: string; botSelectorContent?: React.ReactNode }> = ({ token, botSelectorContent }) => {
  const {
    threads,
    spamCount,
    inboxCount,
    doneCount,
    activeThread,
    setActiveThread,
    filter,
    setFilter,
    feedbacksFilter,
    setFeedbacksFilter,
    isLoading,
    hasLoadError,
    reloadThreads,
    setThreads,
    setThreadInteractions,
  } = useContext(ProductsContext);

  const workspaceContext = useWorkspace();
  const vendorId = workspaceContext?.activeWorkspace?.vendorid;
  const queryClient = useQueryClient();
  const { mutateAsync: updateInboxDisposition } = useUpdateInboxDisposition();
  const { user: authUser } = useAuth();
  const authUserIdRef = useRef<string | undefined>(authUser?.id);
  useEffect(() => { authUserIdRef.current = authUser?.id; }, [authUser?.id]);

  const [searchQuery, setSearchQuery] = useState('');
  const [inboxView, setInboxView] = useState<'conversations' | 'visitors'>('conversations');
  const [pendingPouncedThreadId, setPendingPouncedThreadId] = useState<string | null>(null);
  const pendingPouncedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [mailboxView, setMailboxView] = useState<'inbox' | 'done' | 'spam'>('inbox');
  const [isMobileContactOpen, setIsMobileContactOpen] = useState(false);
  const [pendingDispositionThreadIds, setPendingDispositionThreadIds] = useState<Set<string>>(new Set());
  const pendingDispositionThreadIdsRef = useRef<Set<string>>(new Set());
  const mailboxViewRef = useRef<'inbox' | 'done' | 'spam'>('inbox');
  const [moveToast, setMoveToast] = useState<{
    threadId: string;
    from: 'inbox' | 'done';
    to: 'inbox' | 'done';
  } | null>(null);
  const moveToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveActivityRef = useRef<Map<string, number>>(new Map());
  const [liveThreadIds, setLiveThreadIds] = useState<Set<string>>(new Set());
  const [newChatsAboveFold, setNewChatsAboveFold] = useState(0);
  const threadListRef = useRef<HTMLDivElement>(null);
  const activeThreadRef = useRef(activeThread);
  useEffect(() => { activeThreadRef.current = activeThread; }, [activeThread]);

  const setThreadDispositionPending = (threadId: string, pending: boolean) => {
    const next = new Set(pendingDispositionThreadIdsRef.current);
    if (pending) next.add(threadId); else next.delete(threadId);
    pendingDispositionThreadIdsRef.current = next;
    setPendingDispositionThreadIds(next);
    return next;
  };

  const selectThreadAfterRefresh = useCallback((threadId: string) => {
    setPendingPouncedThreadId(threadId);
    if (pendingPouncedTimerRef.current) clearTimeout(pendingPouncedTimerRef.current);
    pendingPouncedTimerRef.current = setTimeout(() => {
      setPendingPouncedThreadId((current) => current === threadId ? null : current);
    }, 5000);
  }, []);

  useEffect(() => {
    if (!pendingPouncedThreadId) return;
    const pouncedThread = threads.find((thread) => thread.threadId === pendingPouncedThreadId);
    if (!pouncedThread) return;
    setActiveThread(pouncedThread);
    setPendingPouncedThreadId(null);
    if (pendingPouncedTimerRef.current) clearTimeout(pendingPouncedTimerRef.current);
  }, [pendingPouncedThreadId, setActiveThread, threads]);

  const handlePounced = useCallback((threadId: string) => {
    const wasNotInboxView = mailboxViewRef.current !== 'inbox';
    selectThreadAfterRefresh(threadId);
    setInboxView('conversations');
    setMailboxView('inbox');
    mailboxViewRef.current = 'inbox';
    if (wasNotInboxView) {
      setThreads([]);
      setActiveThread(undefined);
      setThreadInteractions([]);
    }
    setFilter((current: any) => ({ ...current, view: 'inbox' }));
    queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
    }, 1000);
  }, [queryClient, selectThreadAfterRefresh, setActiveThread, setFilter, setThreadInteractions, setThreads, token]);

  useEffect(() => {
    if (!vendorId) return;
    const room = `bot_inbox_${vendorId}`;
    const joinRoom = () => { socketIo.emit('join_room', { room }); };
    if (socketIo.connected) joinRoom(); else socketIo.once('connect', joinRoom);
    // Re-join on reconnect so events resume after network interruption.
    socketIo.on('connect', joinRoom);

    const handleVisitorActive = (data: { threadId: string; vendorId: number; timestamp: string }) => {
      const now = Date.now();
      liveActivityRef.current.set(data.threadId, now);
      setLiveThreadIds((prev) => {
        const next = new Set(prev);
        next.add(data.threadId);
        return next;
      });
      setThreads((prev) => {
        const idx = prev.findIndex((t) => t.threadId === data.threadId);
        if (idx === -1) {
          queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
          }, 5000);
          return prev;
        }
        if (idx === 0) return prev;
        const updated = [...prev];
        const [thread] = updated.splice(idx, 1);
        return [{ ...thread, date: data.timestamp, lastInteractionAt: data.timestamp }, ...updated];
      });
    };

    const handleNewChat = (data: { threadId: string; vendorId: number; timestamp: string }) => {
      liveActivityRef.current.set(data.threadId, Date.now());
      setLiveThreadIds((prev) => {
        const next = new Set(prev);
        next.add(data.threadId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
      const listEl = threadListRef.current;
      if (listEl && listEl.scrollTop > 24) {
        setNewChatsAboveFold((prev) => prev + 1);
      }
    };

    const handleAIResponded = (data: {
      threadId: string;
      query: string;
      ai: string;
      timestamp: string;
      spamDisposition?: Pick<ISpamDisposition, 'label' | 'confidence'> | null;
    }) => {
      if (isHighConfidenceSpam(data.spamDisposition)) {
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
        if (mailboxViewRef.current !== 'spam') {
          setThreads((prev) => prev.filter((thread) => thread.threadId !== data.threadId));
          liveActivityRef.current.delete(data.threadId);
          setLiveThreadIds((prev) => {
            const next = new Set(prev);
            next.delete(data.threadId);
            return next;
          });
          if (activeThreadRef.current?.threadId === data.threadId) {
            setActiveThread(undefined);
            setThreadInteractions([]);
          }
          return;
        }
      }

      setThreads((prev) => {
        const idx = prev.findIndex((t) => t.threadId === data.threadId);
        if (idx === -1) {
          queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
          return prev;
        }
        const updated = [...prev];
        const thread = { ...updated[idx] };
        const interactions = thread.interactions ? [...thread.interactions] : [];
        // Fill the row this answer belongs to (prefer exact question match, else
        // oldest unanswered — see findPendingInteractionIndex), so the "AI is
        // typing" indicator clears under backend query drift without mis-pairing
        // when multiple turns are pending. (#2720, follow-up to mb-server #3002)
        const fillIdx = findPendingInteractionIndex(interactions, data.query);
        if (fillIdx !== -1) {
          interactions[fillIdx] = { ...interactions[fillIdx], ai: data.ai, timestamp: data.timestamp };
        } else {
          interactions.push({ id: Date.now(), user: data.query, ai: data.ai, timestamp: data.timestamp, keywords: '' });
        }
        thread.interactions = interactions;
        thread.date = data.timestamp;
        thread.lastInteractionAt = data.timestamp;
        updated[idx] = thread;
        return updated;
      });

      if (activeThreadRef.current?.threadId === data.threadId) {
        setThreadInteractions((prev) => {
          const fillIdx = findPendingInteractionIndex(prev, data.query);
          if (fillIdx !== -1) {
            const next = [...prev];
            next[fillIdx] = { ...next[fillIdx], ai: data.ai, timestamp: data.timestamp };
            return next;
          }
          const isDupe = prev.some((i) => i.ai === data.ai && i.timestamp === data.timestamp);
          if (isDupe) return prev;
          return [...prev, { id: Date.now(), user: data.query, ai: data.ai, timestamp: data.timestamp, keywords: '' }];
        });
      }
    };

    const handleVisitorMessage = (data: { threadId: string; query: string; timestamp: string }) => {
      // A genuinely new visitor reply reopens a Done conversation on the
      // server. Duplicate-turn events do not write, so let the refetch result
      // decide whether the row moves instead of causing a remove/reappear flicker.
      if (mailboxViewRef.current === 'done') {
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
        return;
      }
      setThreads((prev) => {
        const idx = prev.findIndex((t) => t.threadId === data.threadId);
        if (idx === -1) {
          // New thread we don't know about yet — refetch so the visitor's message
          // shows in the inbox immediately rather than waiting for AI to respond.
          queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
          return prev;
        }
        const updated = [...prev];
        const thread = { ...updated[idx] };
        const interactions = thread.interactions ? [...thread.interactions] : [];

        // Only append if it's not a duplicate
        const isDupe = interactions.some(i => i.user === data.query && i.timestamp === data.timestamp);
        if (isDupe) return prev;

        interactions.push({ id: Date.now(), user: data.query, ai: '', timestamp: data.timestamp, keywords: '' });
        thread.interactions = interactions;
        thread.date = data.timestamp;
        thread.lastInteractionAt = data.timestamp;

        // Move to top if not already there
        if (idx > 0) {
          updated.splice(idx, 1);
          return [thread, ...updated];
        }
        updated[idx] = thread;
        return updated;
      });
    };

    // Keeps the threads cache current for events that originate from teammates.
    // Without this, switching away from a thread mid-session and back loses messages
    // until the next API refetch (or page refresh).
    const appendInteractionToThreadsCache = (
      threadId: string,
      interaction: { id: number; ai: string; user: string; keywords: string; event?: string; sender?: string; timestamp: string },
      dedupePredicate: (i: any) => boolean,
    ) => {
      setThreads((prev) => {
        const idx = prev.findIndex((t) => t.threadId === threadId);
        if (idx === -1) {
          queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
          return prev;
        }
        const updated = [...prev];
        const thread = { ...updated[idx] };
        const interactions = thread.interactions ? [...thread.interactions] : [];
        if (interactions.some(dedupePredicate)) return prev;
        interactions.push(interaction);
        thread.interactions = interactions;
        thread.date = interaction.timestamp;
        thread.lastInteractionAt = interaction.timestamp;
        updated[idx] = thread;
        return updated;
      });
    };

    const handleSdrMessageCache = (data: { threadId: string; sdrName: string; senderUserId: string; message: string; timestamp: string }) => {
      // Sender's own messages already updated the cache via the optimistic-send path
      // in ThreadDetails. Skip here to avoid duplicates from the socket roundtrip.
      if (authUserIdRef.current && data.senderUserId === authUserIdRef.current) return;
      appendInteractionToThreadsCache(
        data.threadId,
        { id: Date.now(), ai: data.message, user: '', keywords: '', event: 'human_message', sender: data.sdrName, timestamp: data.timestamp },
        (i) => i.ai === data.message && i.timestamp === data.timestamp,
      );
    };

    const handleHumanJoinedCache = (data: { threadId: string; sdrName?: string; timestamp: string }) => {
      if (!data.sdrName) return;
      const text = `${data.sdrName} joined the conversation.`;
      appendInteractionToThreadsCache(
        data.threadId,
        { id: Date.now(), ai: text, user: '', keywords: '', event: 'agent_joined', sender: data.sdrName, timestamp: data.timestamp || new Date().toISOString() },
        (i) => i.event === 'agent_joined' && i.ai === text,
      );
    };

    const handleHumanLeftCache = (data: { threadId: string; sdrName?: string; timestamp: string }) => {
      if (!data.sdrName) return;
      const text = `${data.sdrName} left the conversation. AI resumed.`;
      appendInteractionToThreadsCache(
        data.threadId,
        { id: Date.now(), ai: text, user: '', keywords: '', event: 'agent_left', sender: data.sdrName, timestamp: data.timestamp || new Date().toISOString() },
        (i) => i.event === 'agent_left' && i.ai === text,
      );
    };

    // Visitor closed their tab / navigated away. Server emits this after a 5s
    // debounce so brief network blips and reloads don't flicker the indicator.
    // Pairs with `bot:visitor_active` / `bot:visitor_message` which turn it on.
    const handleVisitorOffline = (data: { threadId?: string; userIdToken?: string; timestamp: string }) => {
      const tid = data.threadId;
      if (!tid) return;
      liveActivityRef.current.delete(tid);
      setLiveThreadIds((prev) => {
        if (!prev.has(tid)) return prev;
        const next = new Set(prev);
        next.delete(tid);
        return next;
      });
    };

    // Visitor closed their tab during an active handoff. mb-server already
    // toggles the live dot via `bot:visitor_offline`; this event additionally
    // injects a `visitor_disconnected` system pill into the transcript so the
    // SDR sees the disconnect in the conversation flow (matching what
    // Slack/Teams shows). The interaction is also persisted server-side, so
    // a refetch / reload renders the same pill.
    const handleVisitorDisconnected = (data: { threadId: string; timestamp: string }) => {
      appendInteractionToThreadsCache(
        data.threadId,
        { id: Date.now(), ai: 'Visitor disconnected from the chat.', user: '', keywords: '', event: 'visitor_disconnected', sender: undefined, timestamp: data.timestamp || new Date().toISOString() },
        (i) => i.event === 'visitor_disconnected',
      );
    };

    // In-app SDR session was force-released because the visitor never returned
    // (15-minute server-side timeout) or the hard TTL elapsed. Treat as a
    // standard human-left for UI purposes + drop the live indicator.
    const handleVisitorTimedOut = (data: { threadId: string; sdrName?: string; reason?: string; timestamp: string }) => {
      const tid = data.threadId;
      if (!tid) return;
      liveActivityRef.current.delete(tid);
      setLiveThreadIds((prev) => {
        if (!prev.has(tid)) return prev;
        const next = new Set(prev);
        next.delete(tid);
        return next;
      });
      if (data.sdrName) {
        const text = `${data.sdrName} was released automatically — visitor offline. AI resumed.`;
        appendInteractionToThreadsCache(
          tid,
          { id: Date.now(), ai: text, user: '', keywords: '', event: 'agent_auto_left', sender: data.sdrName, timestamp: data.timestamp || new Date().toISOString() },
          (i) => i.event === 'agent_auto_left' && i.ai === text,
        );
      }
    };

    const handleConversationUpdated = (data: {
      threadId?: string;
      type?: string;
      inboxDisposition?: { state?: 'inbox' | 'done' };
    }) => {
      if (data.type !== 'inbox_disposition_updated' || !data.threadId) return;
      // The initiating client already applied this move optimistically. Its
      // socket echo can arrive before React commits the next-thread selection,
      // so replaying it would clear that selection through the still-current
      // activeThreadRef. The mutation's finally block performs the authoritative
      // refetch; disposition events from other clients still reconcile below.
      if (pendingDispositionThreadIdsRef.current.has(data.threadId)) return;
      if (pendingDispositionThreadIdsRef.current.size === 0) {
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
      }
      if (mailboxViewRef.current === 'spam') return;
      if (data.inboxDisposition?.state === mailboxViewRef.current) return;
      setThreads((prev) => prev.filter((thread) => thread.threadId !== data.threadId));
      if (activeThreadRef.current?.threadId === data.threadId) {
        setActiveThread(undefined);
        setThreadInteractions([]);
      }
    };

    socketIo.on('bot:visitor_active', handleVisitorActive);
    socketIo.on('bot:visitor_message', handleVisitorMessage);
    socketIo.on('bot:new_chat', handleNewChat);
    socketIo.on('vendor:new-chat', handleNewChat);
    socketIo.on('bot:ai_responded', handleAIResponded);
    socketIo.on('thread:sdr-message', handleSdrMessageCache);
    socketIo.on('thread:human-joined', handleHumanJoinedCache);
    socketIo.on('thread:human-left', handleHumanLeftCache);
    socketIo.on('bot:visitor_offline', handleVisitorOffline);
    socketIo.on('thread:visitor-disconnected', handleVisitorDisconnected);
    socketIo.on('thread:visitor-timed-out', handleVisitorTimedOut);
    socketIo.on('vendor:chat-updated', handleConversationUpdated);

    const pruneInterval = setInterval(() => {
      const now = Date.now();
      let changed = false;
      liveActivityRef.current.forEach((ts, tid) => {
        if (now - ts > LIVE_WINDOW_MS) {
          liveActivityRef.current.delete(tid);
          changed = true;
        }
      });
      if (changed) setLiveThreadIds(new Set(liveActivityRef.current.keys()));
    }, 30_000);

    return () => {
      socketIo.off('connect', joinRoom);
      socketIo.off('bot:visitor_active', handleVisitorActive);
      socketIo.off('bot:visitor_message', handleVisitorMessage);
      socketIo.off('bot:new_chat', handleNewChat);
      socketIo.off('vendor:new-chat', handleNewChat);
      socketIo.off('bot:ai_responded', handleAIResponded);
      socketIo.off('thread:sdr-message', handleSdrMessageCache);
      socketIo.off('thread:human-joined', handleHumanJoinedCache);
      socketIo.off('thread:human-left', handleHumanLeftCache);
      socketIo.off('bot:visitor_offline', handleVisitorOffline);
      socketIo.off('thread:visitor-disconnected', handleVisitorDisconnected);
      socketIo.off('thread:visitor-timed-out', handleVisitorTimedOut);
      socketIo.off('vendor:chat-updated', handleConversationUpdated);
      clearInterval(pruneInterval);
    };
  }, [vendorId, setThreads, setThreadInteractions, queryClient, token]);

  const filteredThreads = useMemo(() => {
    if (!searchQuery.trim()) return threads;
    const query = searchQuery.toLowerCase();
    return threads.filter((thread) => {
      const titleMatch = thread.title?.toLowerCase().includes(query);
      const emailMatch = thread.userEmail?.toLowerCase().includes(query);
      const companyMatch = thread.companyInfo?.name?.toLowerCase().includes(query);
      const interactionMatch = thread.interactions?.some(
        (interaction) =>
          interaction.user?.toLowerCase().includes(query) ||
          interaction.ai?.toLowerCase().includes(query)
      );
      return titleMatch || emailMatch || companyMatch || interactionMatch;
    });
  }, [threads, searchQuery]);

  const handleTimeFrameChange = (value: string) => {
    setFilter((current: any) => ({ ...current, days: parseInt(value, 10) }));
  };
  const handleMailboxViewChange = (nextView: 'inbox' | 'done' | 'spam') => {
    const currentApiView = mailboxViewRef.current;
    const nextApiView = nextView;
    setMailboxView(nextView);
    mailboxViewRef.current = nextView;
    if (currentApiView !== nextApiView) {
      setThreads([]);
      setActiveThread(undefined);
      setThreadInteractions([]);
    }
    setFilter((current: any) => ({
      ...current,
      view: nextApiView,
    }));
  };
  const handleFeedbackChange = (value: string) => { setFeedbacksFilter([parseInt(value, 10)]); };

  const showMoveToast = (nextToast: NonNullable<typeof moveToast>) => {
    if (moveToastTimerRef.current) clearTimeout(moveToastTimerRef.current);
    setMoveToast(nextToast);
    moveToastTimerRef.current = setTimeout(() => setMoveToast(null), 4000);
  };

  useEffect(() => () => {
    if (moveToastTimerRef.current) clearTimeout(moveToastTimerRef.current);
    if (pendingPouncedTimerRef.current) clearTimeout(pendingPouncedTimerRef.current);
  }, []);

  const handleToggleDone = async () => {
    if (!activeThread || mailboxView === 'spam') return;
    if (pendingDispositionThreadIdsRef.current.has(activeThread.threadId)) return;
    const from: 'inbox' | 'done' = activeThread.inboxDisposition?.state === 'done' ? 'done' : 'inbox';
    const to: 'inbox' | 'done' = from === 'inbox' ? 'done' : 'inbox';
    const movedThread = activeThread;
    const currentIndex = threads.findIndex((thread) => thread.threadId === movedThread.threadId);
    const remainingThreads = threads.filter((thread) => thread.threadId !== movedThread.threadId);
    const nextThread = getNextVisibleThread(filteredThreads, movedThread.threadId);

    setThreadDispositionPending(movedThread.threadId, true);
    setThreads(remainingThreads);
    setActiveThread(nextThread);
    setThreadInteractions(nextThread?.interactions || []);

    try {
      await updateInboxDisposition({ threadId: movedThread.threadId, state: to });
      showMoveToast({ threadId: movedThread.threadId, from, to });
    } catch (error: any) {
      setThreads((current) => restoreThreadAtIndex(current, movedThread, currentIndex));
      if (!activeThreadRef.current) {
        setActiveThread(movedThread);
        setThreadInteractions(movedThread.interactions || []);
      }
      toast.error(
        error?.response?.status === 409
          ? 'Leave the active chat before marking it done.'
          : 'Could not move this conversation. Please try again.',
      );
    } finally {
      const remainingPending = setThreadDispositionPending(movedThread.threadId, false);
      if (remainingPending.size === 0) {
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
      }
    }
  };

  const handleUndoMove = async () => {
    const lastMove = moveToast;
    if (!lastMove || pendingDispositionThreadIdsRef.current.has(lastMove.threadId)) return;
    if (moveToastTimerRef.current) clearTimeout(moveToastTimerRef.current);
    setMoveToast(null);
    setThreadDispositionPending(lastMove.threadId, true);
    try {
      await updateInboxDisposition({ threadId: lastMove.threadId, state: lastMove.from });
      selectThreadAfterRefresh(lastMove.threadId);
      handleMailboxViewChange(lastMove.from);
    } catch {
      toast.error('Could not undo that move. Please try again.');
    } finally {
      const remainingPending = setThreadDispositionPending(lastMove.threadId, false);
      if (remainingPending.size === 0) {
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails, token], exact: false });
      }
    }
  };

  const getInitials = (email?: string, name?: string) => {
    if (name) return name.slice(0, 2).toUpperCase();
    if (email) return email.slice(0, 2).toUpperCase();
    return 'U';
  };

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-white font-inter">
      <div className="flex h-[52px] flex-shrink-0 items-center border-b border-slate-200 px-4">
        {botSelectorContent && (
          <div className="mr-4 flex flex-shrink-0 items-center border-r border-slate-200 pr-4">
            {botSelectorContent}
          </div>
        )}
        <div className="inline-flex flex-shrink-0 items-center rounded-full bg-slate-100 p-[3px]">
          <button
            type="button"
            onClick={() => setInboxView('conversations')}
            className={cn(
              'inline-flex h-[30px] items-center gap-[7px] rounded-full px-3.5 text-[13px] font-medium transition-colors',
              inboxView === 'conversations' ? 'bg-white font-semibold text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800',
            )}
          >
            <MessageSquare className="h-3.5 w-3.5" /> Conversations
          </button>
          <button
            type="button"
            onClick={() => setInboxView('visitors')}
            className={cn(
              'inline-flex h-[30px] items-center gap-[7px] rounded-full px-3.5 text-[13px] font-medium transition-colors',
              inboxView === 'visitors' ? 'bg-white font-semibold text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800',
            )}
          >
            <Radio className="h-3.5 w-3.5" /> Live visitors
          </button>
        </div>
        <div className="flex-1" />
        {inboxView === 'conversations' && (
          <div className="flex flex-shrink-0 items-center gap-1">
            <FilterSelect
              value={filter.days.toString()}
              onValueChange={handleTimeFrameChange}
              options={TIME_FRAME_OPTIONS}
              placeholder="Time frame"
              className="w-[126px] rounded-lg border-0 bg-transparent shadow-none hover:bg-slate-50"
              showSearch={false}
              size="compact"
            />
            <FilterSelect
              value={feedbacksFilter[0]?.toString() || FEEDBACK_FILTER_VALUE.ALL.toString()}
              onValueChange={handleFeedbackChange}
              options={FEEDBACK_OPTIONS}
              placeholder="Feedback"
              className="w-[128px] rounded-lg border-0 bg-transparent shadow-none hover:bg-slate-50"
              showSearch={false}
              size="compact"
            />
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {inboxView === 'visitors' ? (
          <LiveVisitorsPanel onPounced={handlePounced} />
        ) : (
          <>
            <aside className="flex w-[clamp(220px,22vw,300px)] flex-shrink-0 flex-col overflow-hidden border-r border-slate-200 bg-white">
              <div className="flex-shrink-0 px-3 pt-3">
                <FilterSearch
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search"
                  className="w-full [&_input]:rounded-lg [&_input]:shadow-none"
                  size="compact"
                />
                <nav className="mb-1.5 mt-2.5 flex items-center gap-0.5" aria-label="Conversation mailbox" role="tablist">
                  {([
                    { value: 'inbox' as const, label: 'Inbox', count: inboxCount },
                    { value: 'done' as const, label: 'Done', count: doneCount },
                    { value: 'spam' as const, label: 'Spam', count: spamCount },
                  ]).map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => handleMailboxViewChange(tab.value)}
                      role="tab"
                      aria-selected={mailboxView === tab.value}
                      className={cn(
                        'inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12.5px] font-medium transition-colors',
                        mailboxView === tab.value
                          ? 'bg-slate-100 font-semibold text-slate-900'
                          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800',
                      )}
                    >
                      {tab.label}
                      {tab.count > 0 && (
                        <span className={cn(
                          'rounded-full px-1.5 py-0.5 text-[11px] font-medium leading-none text-slate-500',
                          mailboxView === tab.value ? 'bg-white' : 'bg-slate-100',
                        )}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>

              <div
                ref={threadListRef}
                className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 pt-1"
                onScroll={() => {
                  if ((threadListRef.current?.scrollTop || 0) < 24) setNewChatsAboveFold(0);
                }}
              >
                {newChatsAboveFold > 0 && mailboxView === 'inbox' && (
                  <div className="sticky top-0 z-10 px-1 pb-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        threadListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                        setNewChatsAboveFold(0);
                      }}
                      className="w-full rounded-lg bg-[#0848E7] px-3 py-1.5 text-[11px] font-semibold text-white"
                    >
                      {newChatsAboveFold} new chat{newChatsAboveFold > 1 ? 's' : ''}
                    </button>
                  </div>
                )}

                {isLoading ? (
                  <div className="space-y-1 pt-1">
                    {[...Array(7)].map((_, index) => (
                      <div key={index} className="flex animate-pulse items-start gap-2.5 rounded-lg p-2.5">
                        <div className="h-8 w-8 rounded-full bg-slate-100" />
                        <div className="flex-1 space-y-2 pt-0.5">
                          <div className="h-3 w-3/4 rounded bg-slate-100" />
                          <div className="h-2.5 w-1/2 rounded bg-slate-100" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : hasLoadError && threads.length === 0 ? (
                  <div className="flex flex-col items-center justify-center px-5 py-14 text-center">
                    <MessageSquare className="mb-3 h-5 w-5 text-slate-300" />
                    <p className="text-[13px] font-semibold text-slate-700">Couldn’t load conversations</p>
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-400">Check your connection and try again.</p>
                    <button
                      type="button"
                      onClick={reloadThreads}
                      className="mt-3 rounded-lg border border-slate-200 px-3 py-1.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Try again
                    </button>
                  </div>
                ) : filteredThreads.length === 0 ? (
                  <div className="flex flex-col items-center justify-center px-5 py-14 text-center">
                    <MessageSquare className="mb-3 h-5 w-5 text-slate-300" />
                    <p className="text-[13px] font-semibold text-slate-700">
                      {mailboxView === 'done' ? 'No conversations done in this time range' : mailboxView === 'spam' ? 'No spam detected' : 'No conversations found'}
                    </p>
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
                      {mailboxView === 'done'
                        ? 'Try selecting a longer time frame.'
                        : mailboxView === 'spam'
                          ? 'High-confidence spam appears here automatically.'
                          : 'Try adjusting your search or filters.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {filteredThreads.map((thread) => {
                      const lastInteraction = thread.interactions?.[thread.interactions.length - 1];
                      const lastMessagePreview = (lastInteraction?.ai || lastInteraction?.user || 'New visitor').replace(/<[^>]*>/g, '');
                      const timeAgo = formatThreadAge(thread.date);
                      const isActive = activeThread?.threadId === thread.threadId;
                      const isLive = liveThreadIds.has(thread.threadId);
                      const name = thread.companyInfo?.name || thread.userEmail || thread.title || 'Unknown Visitor';

                      return (
                        <button
                          key={thread.threadId}
                          type="button"
                          onClick={() => setActiveThread(thread)}
                          className={cn(
                            'group flex w-full items-start gap-2.5 rounded-lg p-2.5 text-left transition-colors',
                            isActive ? 'bg-[#EEF3FF]' : 'hover:bg-slate-50',
                          )}
                        >
                          <div className="relative flex-shrink-0">
                            <Avatar className="h-8 w-8 border-0 shadow-none">
                              <AvatarFallback className="bg-slate-100 text-[11px] font-semibold text-slate-600">
                                {getInitials(thread.userEmail, thread.companyInfo?.name)}
                              </AvatarFallback>
                            </Avatar>
                            {isLive && <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="truncate text-[13px] font-semibold text-slate-900">
                                {name}
                              </p>
                              <span className={cn('whitespace-nowrap text-[11px]', isLive ? 'font-semibold text-emerald-600' : 'text-slate-400')}>
                                {timeAgo === 'now' ? 'Active now' : timeAgo}
                              </span>
                            </div>
                            <p className="mt-0.5 truncate text-[12.5px] leading-snug text-slate-500">{lastMessagePreview}</p>
                            {mailboxView === 'spam' && (
                              <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium text-red-600">
                                <ShieldAlert className="h-3 w-3" /> AI-detected spam
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </aside>

            <main className="flex min-w-[320px] flex-1 flex-col overflow-hidden bg-white">
              <ThreadDetails
                isVisitorLive={activeThread?.threadId ? liveThreadIds.has(activeThread.threadId) : false}
                isDone={activeThread?.inboxDisposition?.state === 'done'}
                isUpdatingDisposition={activeThread ? pendingDispositionThreadIds.has(activeThread.threadId) : false}
                onToggleDone={handleToggleDone}
                onOpenContactPanel={() => setIsMobileContactOpen(true)}
              />
            </main>

            <aside className="hidden w-[clamp(230px,23vw,300px)] flex-shrink-0 overflow-y-auto border-l border-slate-200 bg-white md:block">
              <ContactPanel />
            </aside>

            {isMobileContactOpen && (
              <div className="absolute inset-0 z-40 bg-slate-900/20 md:hidden" onClick={() => setIsMobileContactOpen(false)}>
                <aside
                  className="ml-auto h-full w-[min(86vw,300px)] overflow-y-auto border-l border-slate-200 bg-white shadow-xl"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="flex h-12 items-center justify-between border-b border-slate-200 px-4">
                    <span className="text-[13px] font-semibold text-slate-900">Contact details</span>
                    <button
                      type="button"
                      onClick={() => setIsMobileContactOpen(false)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
                      aria-label="Close contact details"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <ContactPanel />
                </aside>
              </div>
            )}
          </>
        )}
      </div>

      {moveToast && (
        <div
          role="status"
          className="absolute bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3.5 rounded-xl bg-slate-900 px-4 py-2.5 text-[13px] font-medium text-white shadow-xl"
        >
          <span>Moved to {moveToast.to === 'done' ? 'Done' : 'Inbox'}</span>
          <button
            type="button"
            onClick={handleUndoMove}
            disabled={pendingDispositionThreadIds.has(moveToast.threadId)}
            className="font-semibold text-[#79EEC1] underline-offset-2 hover:underline disabled:opacity-50"
          >
            Undo
          </button>
        </div>
      )}
    </div>
  );
};

const MessagesHub: FC<IMessagesHubProps> = ({ token, botSelectorContent }) => {
  return (
    <ProductsContextProvider key={token} token={token}>
      <MessagesHubContent token={token} botSelectorContent={botSelectorContent} />
    </ProductsContextProvider>
  );
};

export default MessagesHub;
